/**
 * In-draw snapping for the leather Draw tools — ShapeTool, CutoutTool, HoleTool,
 * and StitchTool (#761). Unlike ConstructionTool's snapping (which only ever
 * targets other construction lines and records coincident constraints), the Draw
 * tools produce *baked* geometry, so all we need is to move the point being drawn
 * onto a nearby existing vertex, a point along an existing edge, or a grid
 * intersection, plus a hit for feedback.
 *
 * Three tiers (#782 added the line tier, mirroring ConstructionTool's #323):
 *  - `gatherDrawSnapTargets` collects every snappable *vertex* AND every
 *    snappable *edge* in the scene once at gesture start (geometry is static
 *    while drawing): piece corners / outlines, hole centres, cut-out & stitch
 *    vertices / edges, and construction-line anchors / edges. Everything is
 *    gathered in DISPLAYED (spread-view) space so it lines up with the cursor
 *    the draw tools resolve against (#786).
 *  - `resolveDrawSnap` pulls a raw cursor point onto the nearest of those targets
 *    within the snap radius. Precedence: **vertex > line > grid** — a nearby
 *    vertex (when `snapPoint` is on) beats a projection onto an edge (when
 *    `snapLine` is on), which in turn beats the cutting-mat grid (when `snapGrid`
 *    is on); if none fire the raw point is returned untouched.
 */
import type paper from "paper";
import type { Project } from "../model/Project";
import { projectConstructionLines, pieceStitchLines } from "../model/Project";
import { pieceDisplayOffset, translatePathData } from "../model/pieceTransform";
import { seamPathData } from "../model/seamGeometry";
import type { EditorContext } from "./EditorContext";
import {
  edgeIntersections,
  nearestIntersection,
} from "../model/edgeIntersections";
import {
  isGridSnapActive,
  isLineSnapActive,
  isPointSnapActive,
  snapPointToGrid,
  type Point2,
} from "./dragSnap";
import { currentPointerKind, snapRadiusFor } from "../canvas/pointerPrecision";

export interface DrawSnapHit {
  pos: Point2;
  kind: "vertex" | "intersection" | "line" | "grid";
}

export interface DrawSnapResult {
  point: Point2;
  hit: DrawSnapHit | null;
}

/** The snappable geometry gathered once per gesture. `vertices` feeds the
 *  vertex tier; `edges` (SVG path-data strings, DISPLAYED space) feed the
 *  line tier's `getNearestLocation` scan. */
/** Geometry left out of `gatherDrawSnapTargets`: the object a gesture is
 *  moving, whose model geometry goes stale as it moves (#1174). A whole part
 *  takes its holes, cutouts and seams with it; a cutout or seam is just that
 *  one object. */
export interface DrawSnapExclusion {
  pieceId?: string;
  cutoutId?: string;
  stitchId?: string;
}

export interface DrawSnapTargets {
  vertices: Point2[];
  edges: string[];
  /** Points where two gathered edges cross (#128). Derived from `edges`, but
   *  cached alongside them: the pairwise solve is far too expensive to redo per
   *  frame, and the geometry is static for the length of a gesture. */
  intersections: Point2[];
}

/** Tolerance (fraction of radius) for calling a curve a circular ARC: the
 *  circle through three of its points has to pass through a fourth too. */
const ARC_FIT_TOL = 0.02;

/** The centre of the circle through three points, or null when they're
 *  collinear (so there is no circle). */
function circumcentre(a: Point2, b: Point2, c: Point2): Point2 | null {
  const d = 2 * (a.x * (b.y - c.y) + b.x * (c.y - a.y) + c.x * (a.y - b.y));
  if (Math.abs(d) < 1e-9) return null;
  const a2 = a.x * a.x + a.y * a.y;
  const b2 = b.x * b.x + b.y * b.y;
  const c2 = c.x * c.x + c.y * c.y;
  return {
    x: (a2 * (b.y - c.y) + b2 * (c.y - a.y) + c2 * (a.y - b.y)) / d,
    y: (a2 * (c.x - b.x) + b2 * (a.x - c.x) + c2 * (b.x - a.x)) / d,
  };
}

/**
 * Snappable centres of one subpath: the middle of a circle or ellipse, and the
 * centre of curvature of every circular ARC in it.
 *
 * Both are found from the GEOMETRY rather than from a stored primitive,
 * because most of the things with a centre don't have one: a leather ellipse is
 * plain path data, a pill's caps are arcs of a shape whose primitive describes
 * an axis, and a rounded corner's fillet belongs to no primitive at all.
 * Reading the shape answers for all of them at once.
 *
 * "Closed and every curve curved" is what separates a circle or ellipse from a
 * rounded rectangle — the latter has straight runs between its fillets, so it
 * offers its fillet centres (useful) and not a bounding-box middle (not).
 */
function centresOf(sub: paper.Path): Point2[] {
  const out: Point2[] = [];
  const curves = sub.curves ?? [];
  if (curves.length === 0) return out;
  if (sub.closed && curves.every((c) => !c.isStraight())) {
    const b = sub.bounds;
    out.push({ x: b.center.x, y: b.center.y });
  }
  for (const c of curves) {
    if (c.isStraight()) continue;
    const p0 = c.getPointAtTime(0);
    const pm = c.getPointAtTime(0.5);
    const p1 = c.getPointAtTime(1);
    const centre = circumcentre(p0, pm, p1);
    if (!centre) continue;
    const r = Math.hypot(p0.x - centre.x, p0.y - centre.y);
    if (!(r > 1e-6)) continue;
    // A bezier that ISN'T a circular arc has a circumcentre too, and it means
    // nothing — so check a fourth point lies on the same circle before
    // offering it.
    const q = c.getPointAtTime(0.25);
    if (
      Math.abs(Math.hypot(q.x - centre.x, q.y - centre.y) - r) / r >
      ARC_FIT_TOL
    ) {
      continue;
    }
    out.push(centre);
  }
  return out;
}

/** Screen-pixel pull radius for the vertex snap — matches ConstructionTool. */
const SNAP_RADIUS_PX = 6;

/** Project-mm distance for the snap radius, inverse-zoomed so the snap zone feels
 *  the same size at any view zoom. Shared with ConstructionTool.snapRadiusMm().
 *  A finger reaches `COARSE_SNAP_SCALE`× as far (#1150). */
export function drawSnapRadiusMm(scope: paper.PaperScope): number {
  const zoom = scope.view.zoom;
  return (
    snapRadiusFor(SNAP_RADIUS_PX, currentPointerKind()) / Math.max(zoom, 0.0001)
  );
}

/** The subpaths of a parsed compound path — mirrors `subpathsOf` in
 *  constructionOutline.ts (a simple path parses to a childless CompoundPath). */
function subpaths(cp: paper.CompoundPath): paper.Path[] {
  return cp.children && cp.children.length > 0
    ? (cp.children as paper.Path[])
    : [cp as unknown as paper.Path];
}

/**
 * Collect every snappable vertex AND edge in the scene, in DISPLAYED (mm)
 * coordinates: piece outline vertices (corners) + outlines, hole centres,
 * cut-out vertices + edges, stitch vertices + edges, and construction-line
 * anchors + edges. Deduped vertices. Call once per gesture — the scene geometry
 * doesn't change while a draw is in progress.
 *
 * Piece-owned geometry is shifted by `pieceDisplayOffset` so a linked part that
 * was moved in Spread view snaps where it is *drawn*, matching how the draw
 * tools hit-test the cursor (#786); construction lines live in construction
 * space and are used as-is. `exclude` leaves out the object a gesture is moving.
 */
export function gatherDrawSnapTargets(
  project: Project,
  scope: paper.PaperScope,
  partLayoutView: "spread" | "assembled" = "assembled",
  exclude: DrawSnapExclusion = {},
): DrawSnapTargets {
  const pts: Point2[] = [];
  const edges: string[] = [];

  const pushPathVertices = (pathData: string) => {
    let cp: paper.CompoundPath;
    try {
      cp = new scope.CompoundPath({ pathData, insert: false });
    } catch {
      return;
    }
    for (const sub of subpaths(cp)) {
      if (!sub.segments) continue;
      for (const seg of sub.segments) {
        pts.push({ x: seg.point.x, y: seg.point.y });
      }
      // Round things have a point worth snapping to that is not ON them: the
      // CENTRE. A circle has no vertex there, so without this the one place a
      // maker measures a circle from — its middle — is the one place the
      // cursor slides straight past.
      for (const c of centresOf(sub)) pts.push(c);
    }
    cp.remove();
  };

  const pushEdge = (pathData: string) => {
    if (pathData) edges.push(pathData);
  };

  for (const piece of project.pieces) {
    if (exclude.pieceId === piece.id) continue;
    const off = pieceDisplayOffset(piece, partLayoutView);
    const shift = (d: string): string =>
      off.dx !== 0 || off.dy !== 0
        ? translatePathData(d, off.dx, off.dy, scope)
        : d;

    const shape = shift(piece.shapePathData);
    pushPathVertices(shape);
    pushEdge(shape);
    for (const hole of piece.holes) {
      pts.push({ x: hole.position.x + off.dx, y: hole.position.y + off.dy });
    }
    for (const cutout of piece.cutouts) {
      if (exclude.cutoutId === cutout.id) continue;
      const d = shift(cutout.pathData);
      pushPathVertices(d);
      pushEdge(d);
    }
    for (const stitch of pieceStitchLines(piece)) {
      if (exclude.stitchId === stitch.id) continue;
      const spts = stitch.points.map((p) => ({
        x: p.x + off.dx,
        y: p.y + off.dy,
      }));
      for (const p of spts) pts.push(p);
      if (spts.length >= 2) {
        // The seam's real shape (#982): line snap projects onto the EDGE, so a
        // curved seam has to offer its curve or the snap lands on a chord that
        // isn't where the seam is drawn.
        pushEdge(seamPathData({ ...stitch, points: spts }));
      }
    }
  }
  for (const line of projectConstructionLines(project)) {
    // Vertices of every line stay snappable (unchanged from #761); the edge
    // tier skips hidden scaffolding, matching ConstructionTool's line snap.
    pushPathVertices(line.pathData);
    if (line.visible !== false) pushEdge(line.pathData);
  }

  return {
    vertices: dedupe(pts),
    edges,
    intersections: edgeIntersections(edges, scope),
  };
}

/** Drop points that coincide to within 1e-3 mm (shared corners of adjacent
 *  edges parse to two identical vertices). */
function dedupe(pts: Point2[]): Point2[] {
  const seen = new Set<string>();
  const out: Point2[] = [];
  for (const p of pts) {
    const key = `${Math.round(p.x * 1000)},${Math.round(p.y * 1000)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}

/** Nearest point ALONG any gathered edge within `radius`, or null. Parses each
 *  edge path once and projects `p` onto it via `getNearestLocation` — the same
 *  approach ConstructionTool's line snap (#323) uses, so a drawn vertex can land
 *  anywhere on an existing edge (including its endpoints), not just its corners. */
function nearestPointOnEdges(
  scope: paper.PaperScope,
  p: Point2,
  edges: ReadonlyArray<string>,
  radius: number,
): Point2 | null {
  const cur = new scope.Point(p.x, p.y);
  let best: Point2 | null = null;
  let bestDist = radius;
  for (const pathData of edges) {
    let cp: paper.CompoundPath;
    try {
      cp = new scope.CompoundPath({ pathData, insert: false });
    } catch {
      continue;
    }
    for (const sub of subpaths(cp)) {
      const loc = sub.getNearestLocation(cur);
      if (!loc) continue;
      const d = loc.point.getDistance(cur);
      if (d <= bestDist) {
        bestDist = d;
        best = { x: loc.point.x, y: loc.point.y };
      }
    }
    cp.remove();
  }
  return best;
}

/**
 * Resolve a raw model point to a snapped one. Precedence: vertex snap (nearest
 * target within the snap radius, when `snapPoint` is active) beats line snap
 * (nearest point along an edge, when `snapLine` is active), which beats grid snap
 * (when `snapGrid` is active); if none fire the raw point is returned with a null
 * hit. The hit describes what the point landed on, for the on-canvas indicator.
 */
export function resolveDrawSnap(
  ctx: EditorContext,
  raw: Point2,
  targets: DrawSnapTargets,
): DrawSnapResult {
  const settings = ctx.settings;
  const scope = ctx.scene.getPaperScope();
  const radius = drawSnapRadiusMm(scope);

  // Tier 1 — vertex snap: nearest existing vertex within the radius.
  if (isPointSnapActive(settings) && targets.vertices.length > 0) {
    const r2 = radius * radius;
    let best: Point2 | null = null;
    let bestD2 = Infinity;
    for (const t of targets.vertices) {
      const d2 = (t.x - raw.x) ** 2 + (t.y - raw.y) ** 2;
      if (d2 <= r2 && d2 < bestD2) {
        bestD2 = d2;
        best = t;
      }
    }
    if (best) {
      const pos = { x: best.x, y: best.y };
      return { point: pos, hit: { pos, kind: "vertex" } };
    }
  }

  // Tier 2 — intersection snap (#128): where two edges CROSS. Sits inside the
  // line tier because a crossing exists only because lines do — someone who
  // turns Line snap off doesn't want line-derived targets — and matches the
  // precedent `angleLineEnd` already set by treating a ray×line crossing as
  // line-snap territory. Ahead of the projection below because a crossing is
  // the more specific answer: "this exact point" beats "somewhere along here",
  // the same reason a vertex beats both.
  if (isLineSnapActive(settings) && targets.intersections.length > 0) {
    const cross = nearestIntersection(raw, targets.intersections, radius);
    if (cross)
      return { point: cross, hit: { pos: cross, kind: "intersection" } };
  }

  // Tier 3 — line snap: nearest point along an existing edge (#782). A nearby
  // vertex or crossing already won above; a line projection here beats the grid.
  if (isLineSnapActive(settings) && targets.edges.length > 0) {
    const onLine = nearestPointOnEdges(scope, raw, targets.edges, radius);
    if (onLine) {
      const pos = { x: onLine.x, y: onLine.y };
      return { point: pos, hit: { pos, kind: "line" } };
    }
  }

  // Tier 3 — grid snap.
  if (isGridSnapActive(settings)) {
    const grid = ctx.scene.getMatGridInfo();
    if (grid) {
      const pos = snapPointToGrid(raw, grid);
      return { point: pos, hit: { pos, kind: "grid" } };
    }
  }

  return { point: { x: raw.x, y: raw.y }, hit: null };
}
